import React, { useState, useCallback } from 'react';

export interface HumiditySettingStyle03Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  marks?: number[];
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  label?: string;
}

/**
 * HumiditySetting Style 03 - Slider with Marks
 * Horizontal slider with percentage marks below
 */
export const Style03_SliderMarks: React.FC<HumiditySettingStyle03Props> = ({
  value,
  min = 30,
  max = 80,
  step = 5,
  marks = [30, 40, 50, 60, 70, 80],
  disabled = false,
  loading = false,
  onChange,
  onChangeEnd,
  label = '目標濕度'
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  const displayValue = isDragging ? localValue : value;
  const percentage = ((displayValue - min) / (max - min)) * 100;

  // 拖曳時只更新本地狀態（UI），不發送命令
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    // 不在這裡調用 onChange，等放開時再發送
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  // 放開時才發送命令（防抖）
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // 使用 onChangeEnd 或 onChange 發送最終值
    if (onChangeEnd) {
      onChangeEnd(localValue);
    } else {
      onChange(localValue);
    }
  }, [localValue, onChange, onChangeEnd]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="text-sm font-bold text-blue-400">
          {loading ? '...' : `${displayValue}%`}
        </span>
      </div>

      {/* Slider Container */}
      <div className="px-4 py-4 bg-slate-800/30 rounded-2xl border border-slate-700/30">
        {/* Slider Track - Custom implementation without native thumb */}
        <div 
          className="relative h-2 rounded-full cursor-pointer"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #334155 ${percentage}%, #334155 100%)`
          }}
        >
          {/* Invisible range input for interaction */}
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={displayValue}
            onChange={handleChange}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            disabled={disabled || loading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            style={{ margin: 0 }}
          />
          
          {/* Custom thumb indicator */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg border-2 border-blue-400 pointer-events-none transition-all"
            style={{ left: `calc(${percentage}% - 10px)` }}
          />
        </div>

        {/* Marks */}
        <div className="flex justify-between mt-3 px-1">
          {marks.map((mark) => (
            <span 
              key={mark} 
              className={`text-xs ${mark === displayValue ? 'text-blue-400 font-bold' : 'text-slate-500'}`}
            >
              {mark}%
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Style03_SliderMarks;
